import { useState, type FormEvent } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  KeyRound,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  ShieldCheck,
  Unlock,
  UserMinus,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CubixApiError } from '@/lib/api/types';
import { cn } from '@/lib/utils';

import {
  ALL_ROLES,
  type CubixRole,
  type UserResponse,
  type UserStatus,
} from './types';
import {
  useAdminResetPassword,
  useCreateUser,
  useSetUserStatus,
  useUsersList,
} from './api';
import { usePermissionsMatrix } from '@/features/permissions/api';

export function UsersPage() {
  const list = useUsersList();
  const [resetTarget, setResetTarget] = useState<UserResponse | null>(null);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">
            Manage who can access this tenant. Admin role required.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => list.refetch()}
          disabled={list.isFetching}
        >
          {list.isFetching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Reload
        </Button>
      </header>

      <CreateUserCard />

      {list.isError ? <ApiErrorBanner error={list.error} /> : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Active users</CardTitle>
          <CardDescription>
            {list.data ? `${list.data.length} user(s) in this tenant.` : 'Loading…'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <UsersTable
            users={list.data ?? []}
            loading={list.isLoading}
            onReset={(u) => setResetTarget(u)}
          />
        </CardContent>
      </Card>

      {resetTarget ? (
        <ResetPasswordCard
          target={resetTarget}
          onClose={() => setResetTarget(null)}
        />
      ) : null}
    </div>
  );
}

// ── Create form ─────────────────────────────────────────────────────

function CreateUserCard() {
  const create = useCreateUser();
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<CubixRole>('ALM_ANALYST');

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    create.mutate(
      { userId, email, fullName: fullName || undefined, password, role },
      {
        onSuccess: () => {
          setUserId('');
          setEmail('');
          setFullName('');
          setPassword('');
          setRole('ALM_ANALYST');
        },
      },
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Create a new user</CardTitle>
        <CardDescription>
          Min 8-char password. Same email cannot exist in another tenant.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-3 md:grid-cols-2" onSubmit={handleSubmit}>
          <Field label="User ID (login slug)" htmlFor="cu-userId">
            <Input
              id="cu-userId"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="alice.dupont"
              required
              minLength={3}
              pattern="^[a-z0-9._-]+$"
            />
          </Field>
          <Field label="Email" htmlFor="cu-email">
            <Input
              id="cu-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alice@bank.ma"
              required
            />
          </Field>
          <Field label="Full name (optional)" htmlFor="cu-name">
            <Input
              id="cu-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </Field>
          <Field label="Initial password" htmlFor="cu-pwd">
            <Input
              id="cu-pwd"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
          </Field>
          <Field label="Role" htmlFor="cu-role">
            <RoleSelect
              id="cu-role"
              value={role}
              onChange={(next) => setRole(next as CubixRole)}
            />
          </Field>

          <div className="md:col-span-2">
            {create.isError ? <ApiErrorBanner error={create.error} /> : null}
            {create.isSuccess ? (
              <p className="mb-2 flex items-center gap-1.5 text-sm text-emerald-600">
                <CheckCircle2 className="h-4 w-4" />
                User created.
              </p>
            ) : null}
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Create user
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Table ───────────────────────────────────────────────────────────

function UsersTable({
  users,
  loading,
  onReset,
}: {
  users: UserResponse[];
  loading: boolean;
  onReset: (u: UserResponse) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left font-medium">Email</th>
            <th className="px-4 py-2 text-left font-medium">User ID</th>
            <th className="px-4 py-2 text-left font-medium">Roles</th>
            <th className="px-4 py-2 text-left font-medium">Status</th>
            <th className="px-4 py-2 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
              </td>
            </tr>
          ) : users.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                No users yet — create one above.
              </td>
            </tr>
          ) : (
            users.map((u) => (
              <UserRow key={u.userId} user={u} onReset={() => onReset(u)} />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function UserRow({ user, onReset }: { user: UserResponse; onReset: () => void }) {
  const status = useSetUserStatus(user.userId);
  const isAdminRow = user.roles.includes('ALM_ADMIN');

  return (
    <tr className="border-b last:border-0">
      <td className="px-4 py-2 font-medium">{user.email}</td>
      <td className="px-4 py-2 text-muted-foreground">{user.userId}</td>
      <td className="px-4 py-2">
        <div className="flex flex-wrap gap-1">
          {user.roles.map((r) => (
            <span
              key={r}
              className={cn(
                'rounded px-1.5 py-0.5 text-[11px] font-medium',
                r === 'ALM_ADMIN'
                  ? 'bg-primary/10 text-primary'
                  : 'bg-secondary text-secondary-foreground',
              )}
            >
              {r}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-2">
        <StatusBadge status={user.status} />
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={onReset} title="Reset password">
            <KeyRound className="h-4 w-4" />
          </Button>
          {user.status === 'ACTIVE' ? (
            <Button
              variant="ghost"
              size="sm"
              title="Lock account"
              disabled={isAdminRow || status.isPending}
              onClick={() => status.mutate({ status: 'LOCKED' })}
            >
              <Lock className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              title="Reactivate account"
              disabled={status.isPending}
              onClick={() => status.mutate({ status: 'ACTIVE' })}
            >
              <Unlock className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            title="Disable account"
            disabled={isAdminRow || status.isPending || user.status === 'DISABLED'}
            onClick={() => status.mutate({ status: 'DISABLED' })}
          >
            <UserMinus className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: UserStatus }) {
  const tone =
    status === 'ACTIVE'
      ? 'bg-emerald-500/10 text-emerald-700'
      : status === 'LOCKED'
        ? 'bg-amber-500/10 text-amber-700'
        : 'bg-muted text-muted-foreground';
  return (
    <span className={cn('rounded px-1.5 py-0.5 text-[11px] font-medium', tone)}>
      {status}
    </span>
  );
}

// ── Reset password panel ────────────────────────────────────────────

function ResetPasswordCard({
  target,
  onClose,
}: {
  target: UserResponse;
  onClose: () => void;
}) {
  const reset = useAdminResetPassword(target.userId);
  const [pwd, setPwd] = useState('');

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    reset.mutate(
      { newPassword: pwd },
      {
        onSuccess: () => {
          setPwd('');
        },
      },
    );
  }

  return (
    <Card className="border-primary/40">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4 text-primary" />
          Reset password for {target.email}
        </CardTitle>
        <CardDescription>
          Min 8 characters. Communicate the new password securely.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="flex flex-wrap items-end gap-3" onSubmit={handleSubmit}>
          <div className="flex-1 min-w-60 space-y-1.5">
            <Label htmlFor="rp-new">New password</Label>
            <Input
              id="rp-new"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              minLength={8}
              required
            />
          </div>
          <Button type="submit" disabled={reset.isPending}>
            {reset.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Set password
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </form>
        {reset.isError ? <ApiErrorBanner error={reset.error} /> : null}
        {reset.isSuccess ? (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-emerald-600">
            <CheckCircle2 className="h-4 w-4" /> Password updated.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ── Tiny shared bits ────────────────────────────────────────────────

/**
 * Role <select> populated from the dynamic role catalog
 * (/api/v1/permissions). Falls back to the hardcoded ALL_ROLES list if
 * the matrix is still loading or fails — so the form is always usable.
 * Custom roles created via /settings/permissions appear here
 * automatically without any code change.
 */
function RoleSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const matrix = usePermissionsMatrix();
  const liveRoles = matrix.data?.roles?.map((r) => r.roleName);
  const options = liveRoles && liveRoles.length > 0 ? liveRoles : ALL_ROLES;
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      {options.map((r) => (
        <option key={r} value={r}>{r}</option>
      ))}
    </select>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}

function ApiErrorBanner({ error }: { error: CubixApiError | Error }) {
  const isCubix = error instanceof CubixApiError;
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{error.message}</p>
        {isCubix ? (
          <p className="mt-0.5 text-xs opacity-80">
            code{' '}
            <code className="rounded bg-background/60 px-1 py-0.5">
              {error.code}
            </code>
            {error.field ? <> · field {error.field}</> : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}
